import React from 'react';

interface TermsConditionsProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsConditions: React.FC<TermsConditionsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-rs-black/80 z-50 flex items-center justify-center">
      <div className="bg-rs-black border border-rs-jet rounded-lg relative max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
        <div className="sticky top-0 bg-rs-black border-b border-rs-jet p-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-rs-off-white">Terms and Conditions</h2>
          <button
            onClick={onClose}
            className="text-3xl text-rs-off-white hover:text-rs-dark-green cursor-pointer"
          >
            &times;
          </button>
        </div>

        <div className="p-6 text-rs-off-white text-sm leading-relaxed">
          <p><strong className="text-rs-dark-green">Effective Date:</strong> May 1, 2025</p>
          <p className="mb-4">
            <strong className="text-rs-dark-green">Welcome to ResinSmart!</strong> By accessing or using our website located at www.resinsmart.ai ("Website") and subscribing to our services, you agree to comply with and be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use the Website or our services.
          </p>

          <hr className="my-4 border-rs-jet" />

          <ol className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">1. Definitions</strong>
              <ul className="mt-2 ml-4 flex flex-col gap-2">
                <li><strong className="text-rs-dark-green">"Service(s)"</strong> refers to the subscription-based products and services offered by ResinSmart as it relates to advising on the purchase and negotiation of resin.</li>
                <li><strong className="text-rs-dark-green">"User"</strong> refers to the individual or entity that subscribes to the Service.</li>
              </ul>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={2} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">2. Subscription and Billing</strong>
              <div className="mt-2 flex flex-col gap-2">
                <p><strong className="text-rs-dark-green">2.1 Subscription Fees</strong>: By subscribing to our Service, you agree to pay the recurring subscription fee indicated during the sign-up process. Fees are billed on a monthly/quarterly/annual basis, depending on level of service.</p>
                <p><strong className="text-rs-dark-green">2.2 Trial Period</strong>: If we offer a free trial, the duration of the trial period will be clearly stated. You will not be charged during the trial period. After the trial period ends, you will be automatically enrolled into a paid subscription unless you cancel before the trial expires.</p>
                <p><strong className="text-rs-dark-green">2.3 Payment Methods</strong>: You agree to provide accurate, up-to-date payment information when subscribing to our Service. You authorize us to charge the subscription fee to the payment method on file.</p>
                <p><strong className="text-rs-dark-green">2.4 Automatic Renewals</strong>: Your subscription will automatically renew at the end of each billing period, and you authorize us to charge your payment method accordingly unless you cancel before the renewal date.</p>
                <p><strong className="text-rs-dark-green">2.5 Price Changes</strong>: We reserve the right to change subscription fees at any time. You will be notified of any price changes, and you may choose to cancel your subscription if you do not agree to the new fees.</p>
              </div>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={3} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">3. Service Access and Use</strong>
              <div className="mt-2 flex flex-col gap-2">
                <p><strong className="text-rs-dark-green">3.1 Account Information</strong>: To access our Service, you must create an account and provide accurate and complete information. You are responsible for maintaining the confidentiality of your account and password.</p>
                <p><strong className="text-rs-dark-green">3.2 Account Security</strong>: You are responsible for all activity that occurs under your account, whether or not you authorized the activity. You agree to notify us immediately if you believe your account has been compromised.</p>
              </div>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={4} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">4. Cancellation and Refunds</strong>
              <div className="mt-2 flex flex-col gap-2">
                <p><strong className="text-rs-dark-green">4.1 Cancellation Policy</strong>: You may cancel your subscription at any time once your initial service agreement ends.</p>
                <p><strong className="text-rs-dark-green">4.2 Refunds</strong>: We do not offer refunds for any subscription fees already paid, except where required by law or at our sole discretion.</p>
              </div>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={5} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">5. User Responsibilities</strong>
              <div className="mt-2 flex flex-col gap-2">
                <p><strong className="text-rs-dark-green">5.1 License to Use</strong>: We grant you a non-transferable, non-exclusive license to use the Service for personal or business purposes as permitted under these Terms.</p>
                <p><strong className="text-rs-dark-green">5.2 Prohibited Uses</strong>: You agree not to misuse the Service by engaging in any of the following activities:</p>
                <ul className="ml-4 mt-2 flex flex-col gap-1">
                  <li>• Violating any applicable laws or regulations.</li>
                  <li>• Infringing on the intellectual property rights of others.</li>
                  <li>• Attempting to gain unauthorized access to any system or account.</li>
                  <li>• Distributing malware, viruses, or other harmful code.</li>
                </ul>
                <p><strong className="text-rs-dark-green">5.3 Content Ownership</strong>: All content provided through the Service is owned by Resin Technology, ResinSmart or its licensors. You may not reproduce, distribute, or create derivative works without our permission.</p>
              </div>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={6} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">6. Prohibited Conduct</strong>
              <div className="mt-2 flex flex-col gap-2">
                <p><strong className="text-rs-dark-green">6.1 Termination by Us</strong>: We may suspend or terminate your access to the Service at our sole discretion if you violate these Terms, engage in fraudulent activity, or fail to pay your subscription fees.</p>
                <p><strong className="text-rs-dark-green">6.2 Termination by You</strong>: You may terminate your subscription by following the cancellation procedures outlined in Section 4.</p>
              </div>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={7} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">7. Privacy Policy</strong>
              <p className="mt-2">Your use of the Service is also governed by our Privacy Policy, which can be found <a href="//resinsmart.ai/privacy-policy?hsLang=en" className="text-rs-dark-green hover:text-rs-light-green underline">here</a>. By agreeing to these Terms, you consent to the collection and use of your data as described in our Privacy Policy.</p>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={8} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">8. Limitation of Liability</strong>
              <p className="mt-2">To the fullest extent permitted by law, Resin Technology Inc. and ResinSmart shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss of profits, revenue, data, or use arising from or related to your use of the Service.</p>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={9} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">9. Indemnification</strong>
              <p className="mt-2">You agree to indemnify, defend, and hold harmless Resin Technology Inc., ResinSmart and its affiliates, employees, and agents from any claims, losses, liabilities, and expenses (including attorneys' fees) arising out of your use of the Service, violation of these Terms, or infringement of any intellectual property or other rights.</p>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={10} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">10. Governing Law</strong>
              <p className="mt-2">These Terms are governed by and construed in accordance with the laws of Texas, without regard to its conflict of law principles. Any disputes arising from or related to these Terms will be resolved in the courts of Texas.</p>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={11} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">11. Amendments</strong>
              <p className="mt-2">We reserve the right to modify or update these Terms at any time. Any changes will be posted on this page, and the effective date will be updated accordingly. Your continued use of the Service after any changes indicates your acceptance of the new Terms.</p>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <ol start={12} className="mb-4 flex flex-col gap-4">
            <li>
              <strong className="text-rs-dark-green">12. Contact Information</strong>
              <ul className="ml-4 mt-2 flex flex-col gap-1">
                <li>• Email: <a href="mailto:info@rtiglobal.com" className="text-rs-dark-green hover:text-rs-light-green">info@rtiglobal.com</a></li>
                <li>• Phone: 817-569-0252</li>
                <li>• Address: 6618 Bryant Irvin, Suite 200, Fort Worth, TX 76132</li>
              </ul>
            </li>
          </ol>

          <hr className="my-4 border-rs-jet" />

          <h2 className="text-xl font-bold mb-4 text-rs-dark-green">SUBSCRIBER ADDENDUM</h2>

          <p className="mb-4">This addendum is by and between ResinSmart ("Distributor") and ("Subscriber") (the "Subscriber Addendum"). The Distributor and Subscriber are each a "Party" and collectively the "Parties".</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">1. Background</h3>
          <ol type="A" className="mb-4 flex flex-col gap-2">
            <li>Distributor has entered into an agreement with Chicago Mercantile Exchange Inc (or its successors or assignees) ("CME") whereby CME Group has granted the Distributor the right to receive and retransmit CME Licensed Information to Subscriber Group.</li>
            <li>Distributor and Subscriber have entered into an agreement to provide CME Licensed Information and potentially other market data.</li>
            <li>This Addendum sets additional terms for receiving CME Licensed Information.</li>
          </ol>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">2. Definitions</h3>
          <p className="mb-4">Definitions are provided for terms such as Affiliate, Automated Trading System, Benchmark Information, Device, Non-Display Use, and others. These terms clarify usage and obligations under the Addendum.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">3. Subscriber Addendum Term</h3>
          <p className="mb-4">The Addendum begins when CME Licensed Information is first made available and continues until terminated.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">4. Receipt of CME Licensed Information</h3>
          <ol className="mb-4 flex flex-col gap-2">
            <li>1. Subscriber Group may access and display CME Licensed Information for internal business use.</li>
            <li>2. All Non-Display Use requires a direct license with CME.</li>
          </ol>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">5. Internal Controls</h3>
          <p className="mb-4">Subscriber must maintain effective Internal Controls per CME's Information Policies and keep auditable records.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">6. CME Licensed Information</h3>
          <ul className="mb-4 flex flex-col gap-2">
            <li>• No redistribution outside Subscriber Group without CME approval.</li>
            <li>• No defacement or misrepresentation of information.</li>
            <li>• Compliance with laws is mandatory.</li>
            <li>• No derivative works unless licensed.</li>
            <li>• Subscriber acknowledges CME ownership of IP and consents to CME's use of Personal Data.</li>
          </ul>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">7. Subscriber Reporting Obligations</h3>
          <ul className="mb-4 flex flex-col gap-2">
            <li>• Maintain and retain 5 years of access records.</li>
            <li>• Report access via Devices per CME rules.</li>
            <li>• No "usage-based" counting systems allowed.</li>
          </ul>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">8. Audits</h3>
          <p className="mb-4">Distributor or CME may audit Subscriber records, systems, and compliance with this Addendum, with and without notice. Subscriber bears costs for underpayments and missing records.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">9. Termination</h3>
          <p className="mb-4">Termination can occur with 30 days' notice or immediately in certain cases, such as breach or insolvency. Termination of the Distribution Agreement also terminates this Addendum.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">10. Fees</h3>
          <p className="mb-4">Fees are payable to Distributor and possibly CME, depending on the license structure.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">11. Covenants, Representations and Warranties</h3>
          <ul className="mb-4 flex flex-col gap-2">
            <li>• Subscriber confirms authorized receipt and legal use of CME Licensed Information.</li>
          </ul>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">12. CME Group as a Third-Party Beneficiary</h3>
          <p className="mb-4">CME is a third-party beneficiary with rights to enforce the Addendum directly.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">13. Limitation of Liability</h3>
          <ul className="mb-4 flex flex-col gap-2">
            <li>• CME disclaims warranties and limits liability to actual damages or $50, whichever is less.</li>
          </ul>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">14. Confidentiality</h3>
          <p className="mb-4">Confidential information must be protected. Certain exceptions apply, such as legal requirements.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">15. Modification</h3>
          <p className="mb-4">Distributor and CME may modify the Addendum. Subscriber may terminate upon notice of modifications.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">16. Miscellaneous</h3>
          <ul className="mb-4 flex flex-col gap-2">
            <li>• Invalid terms do not affect the rest.</li>
            <li>• Subscriber Addendum overrides conflicting agreements.</li>
          </ul>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">17. Survival</h3>
          <p className="mb-4">Certain terms, including those related to IP, liability, and jurisdiction, survive termination.</p>

          <h3 className="text-lg font-semibold mb-2 text-rs-dark-green">18. Governing Law and Jurisdiction</h3>
          <p className="mb-4">Governing law is Illinois law; jurisdiction is in Cook County, IL and U.S. District Court for the Northern District of Illinois.</p>
        </div>

        <div className="sticky bottom-0 bg-rs-black border-t border-rs-jet p-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 green"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
